import type { Href } from "expo-router";
import { router, useFocusEffect, useLocalSearchParams } from "expo-router";
import { useCallback, useMemo, useRef, useState } from "react";
import { ActivityIndicator, ScrollView, Text, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import { AppHeader } from "../../../../components/AppHeader";
import { Avatar, Badge, Button, Card, EmptyState, Section } from "../../../../components/ui";
import { listCompetitionStudents, listMyCompetitions } from "../../../../lib/campus";
import type { CampusCompetitionRow, CampusCompetitionStudentRow } from "../../../../types/database";

export default function CampusCompetitionDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const [competition, setCompetition] = useState<CampusCompetitionRow | null>(null);
  const [students, setStudents] = useState<CampusCompetitionStudentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const hasLoadedRef = useRef(false);
  const lastLoadIdRef = useRef<string | undefined>(undefined);

  const load = useCallback(async () => {
    if (typeof id === "string" && lastLoadIdRef.current !== id) {
      hasLoadedRef.current = false;
      lastLoadIdRef.current = id;
    }
    if (!hasLoadedRef.current) setLoading(true);
    try {
      if (typeof id !== "string") return;
      const [comps, stu] = await Promise.all([listMyCompetitions(), listCompetitionStudents(id)]);
      setCompetition(comps.find((c) => c.competition_id === id) ?? null);
      setStudents(stu);
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
    }
  }, [id]);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load])
  );

  const closed = useMemo(() => competition?.status === "closed", [competition]);

  if (loading) {
    return (
      <SafeAreaView className="flex-1 items-center justify-center bg-linen">
        <ActivityIndicator size="large" color="#0A2540" />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView className="flex-1 bg-linen" edges={["top", "left", "right"]}>
      <ScrollView className="flex-1 px-4 pt-4" contentContainerStyle={{ paddingBottom: 28 }}>
        <View className="rounded-card border border-white/10 bg-forest px-2 py-2">
          <AppHeader />
        </View>
        <View className="mt-2 self-start">
        <Button
          size="sm"
          variant="ghost-light"
          onPress={() => {
            if (router.canGoBack()) router.back();
            else router.replace("/campus" as Href);
          }}
        >
          Back
        </Button>
        </View>

        <Section overline="Competition" title={competition?.competition_name ?? "Competition"} subtitle={competition?.company_name ?? "Company"}>
          <View className="self-start">
            <Badge status="info">{competition?.status ?? "draft"}</Badge>
          </View>
        </Section>

        <Text className="mt-6 text-lg font-semibold text-text">Students</Text>
        <View className="mt-2 gap-2">
          {students.length === 0 ? (
            <Card variant="light" padding="md"><EmptyState title="No students in this competition" /></Card>
          ) : students.map((row) => (
            <Card
              key={row.student_id}
              variant="light"
              padding="md"
              onPress={() => router.push(`/campus/student/${row.student_id}` as Href)}
            >
              <View className="flex-row items-center gap-3">
                <Avatar name={row.full_name ?? "Student"} />
                <View className="flex-1">
              <Text className="text-sm font-semibold text-text">{row.full_name ?? "Student"}</Text>
              <Text className="mt-0.5 text-xs text-text/70">
                {row.major ?? "—"} · {row.graduation_year ?? "—"} · {row.team_name ?? "No team"}
              </Text>
              <Text className="mt-0.5 text-xs text-text/60">
                Submitted {row.submitted_count} of {row.total_deliverables} ·{" "}
                {closed ? (row.placement != null ? `Placed #${row.placement}` : "Pending") : "Pending"}
              </Text>
                </View>
              </View>
            </Card>
          ))}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}
